/** @format */
import { MonoTypeOperatorFunction, Observable, Subject, Subscription } from 'rxjs';
import { exhaustMap, finalize, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { Mono } from '../mono/monoUtils';

/**
 * RequestQueue is a wrapper of a {@link Subject} (queue$) and its {@link Subscription}, which together
 * are configured to execute defined requests in a specific manner.
 */
export class RequestQueue<P, R> {
    readonly ignoreConcurrent: boolean;
    private readonly queue$: Subject<P>;
    private readonly subscription: Subscription;
    inProgress: boolean = false;

    /**
     * Constructor for RequestQueue. Helper functions, e.g. {@link requestQueueIgnoreConcurrent}, should be preferred
     * over calling this constructor directly.
     *
     * @param ignoreConcurrent Boolean that defines whether concurrent requests should be ignored.
     * @param request Mono returning request function that is executed every time this RequestQueue's next is called.
     * @param resultHandler Function that is passed to the RequestQueue's subscribe call.
     * @param errorHandler MonoTypeOperatorFunction to where errors are piped to.
     * @param destroy$ Either an Observable which closes the queue upon first value emission, or an operator function which
     * can close the queue based on any condition/event (e.g. `takeUntilDestroyed()`).
     */
    constructor(
        ignoreConcurrent: boolean,
        request: (p?: P) => Mono<R>,
        resultHandler: (r: R) => void,
        errorHandler: MonoTypeOperatorFunction<R>,
        destroy$?: Observable<unknown> | MonoTypeOperatorFunction<unknown>,
    ) {
        this.ignoreConcurrent = ignoreConcurrent;
        this.queue$ = new Subject<P>();
        // the following are not actually deprecated -> ignore ESLint and Sonar warnings
        // eslint-disable-next-line deprecation/deprecation
        const mapOperator = this.ignoreConcurrent ? exhaustMap : mergeMap; // NOSONAR
        const pipedQueue$ = this.queue$.pipe(
            tap(() => (this.inProgress = true)),
            mapOperator(p =>
                request(p).pipe(
                    errorHandler,
                    finalize(() => (this.inProgress = false)),
                ),
            ),
        );
        if (destroy$) {
            const operator = typeof destroy$ === 'function' ? destroy$ : takeUntil(destroy$);
            this.subscription = pipedQueue$.pipe(operator).subscribe(resultHandler);
        } else {
            this.subscription = pipedQueue$.subscribe(resultHandler);
        }
    }

    /**
     * Invoke the next request.
     *
     * @param value Argument to be passed to the request in case the related type is other than <code>void</code>
     */
    next(value: P): void {
        this.queue$.next(value);
    }

    /**
     * Alternative way to unsubscribe from the internal subscription in case <code>destroy$</code> was not provided
     * in the constructor.
     */
    unsubscribe(): void {
        this.subscription.unsubscribe();
    }

    /**
     * The closed state of the internal subscription, mainly for testing purposes.
     */
    get closed(): boolean {
        return this.subscription.closed;
    }
}

/**
 * Helper function for constructing a {@link RequestQueue} instance.
 *
 * @param request Mono returning request function that is executed every time the resulting RequestQueue's next is called.
 * @param resultHandler Function that is passed to the resulting RequestQueue's subscribe call.
 * @param errorHandler MonoTypeOperatorFunction to where errors are piped to.
 * @param destroy$ Either an Observable which closes the queue upon first value emission, or an operator function which
 * can close the queue based on any condition/event (e.g. `takeUntilDestroyed()`).
 */
export function requestQueueIgnoreConcurrent<P, R>(
    request: (p?: P) => Mono<R>,
    resultHandler: (r: R) => void,
    errorHandler: MonoTypeOperatorFunction<R>,
    destroy$?: Observable<unknown> | MonoTypeOperatorFunction<unknown>,
): RequestQueue<P, R> {
    return new RequestQueue<P, R>(true, request, resultHandler, errorHandler, destroy$);
}
